"use client"

import { diffWords } from "diff"
import React from "react"

import type { NewProposedBlockEdit } from "@/generated/course-material-api/types.generated"
import DiffFormatter from "@/shared-module/common/components/DiffFormatter"

import { getEditableHoverStyles, getParagraphStyles } from "../styles"
import { useParagraphEditing } from "./hooks/useParagraphEditing"

interface PreviewableParagraphProps {
  id: string
  content: string | null
  textColor?: string
  backgroundColor?: string
  fontSize?: string
  align?: string
  setEdits: React.Dispatch<React.SetStateAction<Map<string, NewProposedBlockEdit>>>
  editedContent: string | null
}

const handleSelectForEditing = () => {
  // This function will be passed to the onClick handler
  // It doesn't need to do anything special now, as the parent component
  // will handle setting the selectedBlockId
}

const PreviewableParagraph: React.FC<PreviewableParagraphProps> = ({
  id,
  content,
  textColor,
  backgroundColor,
  fontSize,
  align,
  setEdits,
  editedContent: propEditedContent,
}) => {
  const { editedContent: hookEditedContent } = useParagraphEditing({
    id,
    editing: true,
    selectedBlockId: null,
    content,
    setEdits,
    isEditingEnabled: false,
  })

  const actualEditedContent = hookEditedContent !== content ? hookEditedContent : propEditedContent

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      handleSelectForEditing()
    }
  }

  const diffChanges = React.useMemo(() => {
    if (content === actualEditedContent) {
      return []
    }
    return diffWords(content ?? "", actualEditedContent ?? "")
  }, [content, actualEditedContent])

  return (
    <p
      className={`${getParagraphStyles(textColor, backgroundColor, fontSize, true, false, align)} ${getEditableHoverStyles(false)}`}
      onClick={handleSelectForEditing}
      onKeyDown={handleKeyDown}
      // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role, jsx-a11y/prefer-tag-over-role -- intentional interactive paragraph; <button> would change layout
      role="button"
      tabIndex={0}
    >
      {diffChanges.length > 0 ? <DiffFormatter changes={diffChanges} /> : content}
    </p>
  )
}

PreviewableParagraph.displayName = "PreviewableParagraph"

export default PreviewableParagraph
